import { ActivityType } from '@/api/models/activity'
import { ActivityPart } from '@/api/models/activityParts'
import {
  ActivityWithAggregate,
  ActivityWorkflowState,
  ResultsDisclosurePolicy,
  ReviewPolicy,
} from '@/api/models/aggregate'
import { checkAfterRevealingDate } from './checkConstraintDates'

const checkScoreIsReached = (activityPart: ActivityPart<ActivityWithAggregate>) => {
  const resultsDisclosureAbove = activityPart.activityConstraint.resultsDisclosureAbove
  const childActivity = activityPart.childActivity

  if (!childActivity || !resultsDisclosureAbove) {
    return true
  }

  // const score = childActivity.activityWorkflowAggregate?.bestActivityRecordScore || 0
  // return score >= passingScore

  // ALMS-9416: in the scope of this ticket, we don't need to respect the resultsDisclosureAbove (it's strange but it's true)
  return true
}

const isInGrading = (activityPart: ActivityPart<ActivityWithAggregate>) => {
  const lastActivityWorkflow = activityPart.childActivity.activityWorkflowAggregate?.lastActivityWorkflow
  return lastActivityWorkflow?.state === ActivityWorkflowState.inGrading
}

const checkAfterRevealingResults: ReviewVisibility = (activityPart) => {
  const constraint = activityPart?.activityConstraint

  if (!constraint) {
    return { isReviewHidden: false }
  }

  if (isInGrading(activityPart)) {
    return { isReviewHidden: true, reviewHiddenReason: 'compositions.constraintValidations.reviewPolicyNotAllowed' }
  }

  const { resultsDisclosurePolicy } = constraint

  switch (resultsDisclosurePolicy) {
    case ResultsDisclosurePolicy.Never:
      return { isReviewHidden: true, reviewHiddenReason: 'compositions.constraintValidations.reviewPolicyNotAllowed' }
    case ResultsDisclosurePolicy.AfterDate:
      if (checkAfterRevealingDate(activityPart)) {
        return { isReviewHidden: false }
      } else {
        return { isReviewHidden: true, reviewHiddenReason: 'compositions.constraintValidations.afterRevealingDate' }
      }
    case ResultsDisclosurePolicy.AfterScore:
      if (activityPart.childActivity.type !== ActivityType.lti) {
        return { isReviewHidden: false }
      }

      if (checkScoreIsReached(activityPart)) {
        return { isReviewHidden: false }
      } else {
        return { isReviewHidden: true, reviewHiddenReason: 'compositions.constraintValidations.afterScoreIsReached' }
      }
    case ResultsDisclosurePolicy.AfterFinish:
      const isFinished = Boolean(activityPart.childActivity.activityWorkflowAggregate?.lastActivityWorkflow?.finished)

      if (!isFinished) {
        return { isReviewHidden: true, reviewHiddenReason: 'compositions.constraintValidations.reviewPolicyNotAllowed' }
      }
      return { isReviewHidden: false }
    default:
      return { isReviewHidden: false }
  }
}

const isCompleted = (activityPart: ActivityPart<ActivityWithAggregate>) => {
  const isSubmitted = Boolean(activityPart.childActivity.activityWorkflowAggregate?.lastActivityWorkflow?.submitted)
  const isFinished = Boolean(activityPart.childActivity.activityWorkflowAggregate?.lastActivityWorkflow?.finished)

  const isComposite = activityPart.childActivity.type === ActivityType.composite

  if (isComposite && isFinished) {
    return true
  }

  if (isSubmitted) {
    return true
  }

  return false
}

type ReviewVisibility = (
  activityPart: ActivityPart<ActivityWithAggregate>,
  ignoreState?: boolean,
) => {
  isReviewHidden: boolean
  reviewHiddenReason?: string
}

export const checkReviewVisibility: ReviewVisibility = (activityPart) => {
  const constraint = activityPart?.activityConstraint

  if (!constraint || !isCompleted(activityPart)) {
    return { isReviewHidden: false, reviewHiddenReason: '' }
  }

  const reviewPolicy = constraint.reviewPolicy

  switch (reviewPolicy) {
    case ReviewPolicy.Never:
      return { isReviewHidden: true, reviewHiddenReason: 'compositions.constraintValidations.reviewPolicyNotAllowed' }
    case ReviewPolicy.AfterSubmission:
      return { isReviewHidden: false }
    case ReviewPolicy.AfterRevealingResults:
      return checkAfterRevealingResults(activityPart)
    default:
      return { isReviewHidden: false }
  }
}
